/**
 * 服务监控页面
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
  max-width: 1600px;
  margin: 0 auto;
`;

const Layout = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const LeftPanel = styled.div`
  flex: 0 0 400px;
  
  @media (max-width: 1024px) {
    flex: none;
  }
`;

const RightPanel = styled.div`
  flex: 1;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: #1a1a2e;
  margin: 0;
`;

const StatusBadge = styled.span`
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => props.$healthy ? '#10b981' : '#ef4444'};
  color: white;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const CardTitle = styled.h3`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  width: ${props => props.$percent}%;
  transition: width 0.3s ease;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  background: ${props => props.$primary ? '#667eea' : props.$danger ? '#ef4444' : props.$success ? '#10b981' : '#6b7280'};
  color: white;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LogContainer = styled.div`
  background: #1e1e1e;
  border-radius: 12px;
  padding: 1.5rem;
  max-height: 500px;
  overflow-y: auto;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.8125rem;
`;

const LogEntry = styled.div`
  padding: 0.5rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  background: ${props => {
    switch (props.$level) {
      case 'error': return 'rgba(239, 68, 68, 0.2)';
      case 'warn': return 'rgba(245, 158, 11, 0.2)';
      case 'info': return 'rgba(59, 130, 246, 0.2)';
      default: return 'transparent';
    }
  }};
  color: ${props => {
    switch (props.$level) {
      case 'error': return '#fca5a5';
      case 'warn': return '#fcd34d';
      case 'info': return '#93c5fd';
      default: return '#d1d5db';
    }
  }};
`;

const LogTime = styled.span`
  color: #6b7280;
  margin-right: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const MemoryBar = styled.div`
  margin-top: 1rem;
`;

const MemoryLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

const ServiceMonitorPage = () => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restarting, setRestarting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/service/status', {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      setStatus(response.data.status);
    } catch (error) {
      console.error('获取服务状态失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/service/logs', {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('获取日志失败:', error);
    }
  }, []);

  const refreshData = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchStatus(), fetchLogs()]);
    setRefreshing(false);
  }, [fetchStatus, fetchLogs]);

  const handleRestart = async () => {
    if (!window.confirm('确定要重启服务吗？重启期间服务将暂时不可用。')) {
      return;
    }
    
    setRestarting(true);
    try {
      await axios.post('/api/admin/service/restart', {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      alert('重启请求已提交，服务将在几秒后重启');
    } catch (error) {
      alert('重启失败: ' + (error.response?.data?.message || error.message));
      setRestarting(false);
    }
  };

  useEffect(() => {
    refreshData();
    const interval = setInterval(refreshData, 10000);
    return () => clearInterval(interval);
  }, [refreshData]);

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    const parts = [];
    if (days > 0) parts.push(`${days}天`);
    if (hours > 0) parts.push(`${hours}小时`);
    if (minutes > 0) parts.push(`${minutes}分钟`);
    return parts.join(' ') || '刚刚启动';
  };

  const getMemoryPercent = () => {
    if (!status?.memory) return 0;
    return Math.round((status.memory.heapUsed / status.memory.heapTotal) * 100);
  };

  const getLogLevelIcon = (level) => {
    switch (level) {
      case 'error': return '❌';
      case 'warn': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '📝';
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return <AdminLayout title="服务监控"><Container>加载中...</Container></AdminLayout>;
  }

  return (
    <AdminLayout title="服务监控">
      <Container>
      <Header>
        <Title>🔧 服务监控</Title>
        <StatusBadge $healthy={status?.uptime > 0}>
          {status?.uptime > 0 ? '运行中' : '异常'}
        </StatusBadge>
      </Header>

      <Layout>
        <LeftPanel>
          <Grid>
            <Card>
              <CardTitle>运行时间</CardTitle>
              <StatValue>{formatUptime(status?.uptime || 0)}</StatValue>
              <StatLabel>服务已连续运行</StatLabel>
            </Card>

            <Card>
              <CardTitle>内存使用</CardTitle>
              <StatValue>{status?.memory?.heapUsed || 0} MB</StatValue>
              <StatLabel>/ {status?.memory?.heapTotal || 0} MB</StatLabel>
              <MemoryBar>
                <MemoryLabel>
                  <span>堆内存</span>
                  <span>{getMemoryPercent()}%</span>
                </MemoryLabel>
                <ProgressBar>
                  <ProgressFill $percent={getMemoryPercent()} />
                </ProgressBar>
              </MemoryBar>
            </Card>

            <Card>
              <CardTitle>进程信息</CardTitle>
              <StatValue style={{ fontSize: '1.25rem' }}>PID: {status?.pid || '-'}</StatValue>
              <StatLabel>Node.js {status?.nodeVersion || '-'}</StatLabel>
              <StatLabel style={{ marginTop: '0.5rem' }}>平台: {status?.platform || '-'}</StatLabel>
            </Card>

            <Card>
              <CardTitle>CPU 使用</CardTitle>
              <StatValue>{Math.round((status?.cpu?.user || 0) / 1000000)} ms</StatValue>
              <StatLabel>用户态 CPU 时间</StatLabel>
            </Card>
          </Grid>

          <Card style={{ marginTop: '1.5rem' }}>
            <CardTitle>快捷操作</CardTitle>
            <ButtonGroup>
              <Button $primary onClick={refreshData} disabled={refreshing}>
                {refreshing ? '刷新中...' : '🔄 刷新状态'}
              </Button>
              <Button $danger onClick={handleRestart} disabled={restarting}>
                {restarting ? '重启中...' : '🔄 重启服务'}
              </Button>
            </ButtonGroup>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '1rem' }}>
              💡 提示: 重启服务后页面将自动刷新，请保存好当前工作
            </p>
          </Card>
        </LeftPanel>

        <RightPanel>
          <Card style={{ height: '100%', minHeight: '600px' }}>
            <CardTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📋 重启日志</span>
              <Button 
                $success 
                onClick={fetchLogs}
                style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
              >
                刷新日志
              </Button>
            </CardTitle>
            
            {logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                暂无重启日志
              </div>
            ) : (
              <LogContainer style={{ maxHeight: 'calc(100% - 60px)' }}>
                {logs.map((log, index) => (
                  <LogEntry key={index} $level={log.level}>
                    <LogTime>{formatTimestamp(log.timestamp)}</LogTime>
                    <strong>{getLogLevelIcon(log.level)}</strong> {log.message}
                    {log.data && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', opacity: 0.7 }}>
                        {JSON.stringify(log.data)}
                      </div>
                    )}
                  </LogEntry>
                ))}
              </LogContainer>
            )}
          </Card>
        </RightPanel>
      </Layout>
      </Container>
    </AdminLayout>
  );
};

export default ServiceMonitorPage;
